import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
} from "../../components/utils";
import { useReverseForfeiture } from "../../queries/esopPlan";
import { Grant } from "../../types/Grant";

interface BulkReverseForfeitureDialogProps {
  open: boolean;
  grants: Grant[];
  onClose: () => void;
}

export default function BulkReverseForfeitureDialog({
  open,
  grants,
  onClose,
}: BulkReverseForfeitureDialogProps) {
  const {
    mutate: reverseForfeiture,
    isLoading,
    error,
    reset,
  } = useReverseForfeiture();

  const totalOptions = grants.reduce(
    (sum, grant) => sum + (grant.optionsForfeited || 0),
    0
  );

  const handleClose = () => {
    if (isLoading) return;
    reset();
    onClose();
  };

  const handleConfirm = () => {
    const grantIds = grants.map((grant) => grant.grantIdentifier);
    reverseForfeiture(grantIds, {
      onSuccess: (data) => {
        const reversedCount = data?.reversed?.length || 0;
        const skippedCount = data?.skipped?.length || 0;
        toast(
          skippedCount > 0
            ? `Reversed forfeiture for ${reversedCount} grant(s). ${skippedCount} grant(s) had nothing to reverse.`
            : `Reversed forfeiture for ${reversedCount} grant(s).`,
          {
            type: reversedCount > 0 ? "success" : "warning",
            autoClose: 3000,
          }
        );
        onClose();
      },
      onError: (err: any) => {
        toast(err?.response?.data?.reason || "Failed to reverse forfeiture", {
          type: "error",
          autoClose: 3000,
        });
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <Box className="bg-white flex flex-col max-h-[85vh]">
        <div className="px-8 text-lg font-medium border-b py-6 shrink-0">
          <h6 className="flex justify-between items-start">
            <div>
              Reverse Forfeiture
              <p className="text-sm font-normal text-gray-500 mt-1">
                {grants.length} grant{grants.length !== 1 ? "s" : ""} selected
                {" · "}
                {totalOptions.toLocaleString()} option(s) to be restored
              </p>
            </div>
            <span
              onClick={handleClose}
              className="cursor-pointer text-gray-400 hover:text-gray-600 text-base font-normal"
            >
              X
            </span>
          </h6>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="flex items-start gap-2 bg-[#FFF3C7] border border-amber-200 rounded-md px-4 py-3 text-sm text-[#BD7D00] mb-5">
            <Icon icon="fe:warning" className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              The expiry date on affected vesting lots will be cleared to
              prevent these options from being forfeited again.
            </span>
          </div>

          {error ? (
            <>
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700 mb-5">
                <Icon
                  icon="mdi:alert-circle-outline"
                  className="w-4 h-4 mt-0.5 shrink-0"
                />
                <span>
                  {(error as any)?.response?.data?.reason ||
                    "Failed to reverse forfeiture"}
                </span>
              </div>
            </>
          ) : (
            <></>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200 text-gray-500 uppercase">
                  <th className="py-2 px-3 text-left font-medium">Employee</th>
                  <th className="py-2 px-3 text-left font-medium">Grant ID</th>
                  <th className="py-2 px-3 text-right font-medium">
                    Options to be Reversed
                  </th>
                </tr>
              </thead>
              <tbody>
                {grants.map((grant) => (
                  <tr
                    key={grant.optionHolderId}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="py-2 px-3 text-gray-700">
                      {grant.employeeName}
                    </td>
                    <td className="py-2 px-3 text-gray-700">
                      {grant.grantIdentifier}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-gray-900">
                      {(grant.optionsForfeited || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="shrink-0 px-8 py-5 border-t border-gray-200 bg-white">
          <HStack className="justify-between items-center">
            <ButtonPrimary1
              type="button"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </ButtonPrimary1>
            <ButtonPrimary
              type="button"
              onClick={handleConfirm}
              loading={isLoading}
              disabled={grants.length === 0}
            >
              Reverse Forfeiture
            </ButtonPrimary>
          </HStack>
        </div>
      </Box>
    </Dialog>
  );
}
